import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FormSection, Stepper, Field, inputCls } from "@/components/Shared";
import DepartmentPicker from "@/components/DepartmentPicker";
import MasterSelect from "@/components/MasterSelect";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export const VEHICLE_EMPTY = {
  // Identifikation
  license_plate: "", brand: "", model: "", vehicle_type: "Transporter", vin: "",
  // Anhänger
  trailer: "",
  // Einsatz
  department: "", department_id: "", firm_id: "", workplace: "", parking_place: "",
  // Termine
  pickerl_date: "", registration_date: "", warranty_expiry: "",
  deregistration_date: "", insurance_date: "", next_service_date: "",
  // Technisch
  power_kw: 0, fuel_type: "Diesel", adblue_percent: 100, current_km: 0,
  consumption_per_100km: 0, consumption_tolerance: 0,
  // Maße & Reifen
  tire_dimension: "", payload_kg: 0, volume_m3: 0,
  inner_width_cm: 0, inner_height_cm: 0, inner_length_cm: 0,
  // Marketing / Misc
  special_features: "", branding: "", vignette: "",
  // Status
  status: "aktiv",
  // Sonstiges
  notes: "",
};

export const VEHICLE_STEPS = ["Identifikation & Anhänger", "Einsatz & Termine", "Technisch & Maße", "Marketing & Notizen"];

const FUEL_TYPES = ["Diesel", "Benzin", "Elektro", "Hybrid", "Erdgas (CNG)", "Flüssiggas (LPG)", "Wasserstoff"];

export default function VehicleFormWizard({ form, setForm, onClose, onSave, editing }) {
  const [step, setStep] = useState(0);
  const [brands, setBrands] = useState([]);
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/fuhrpark-master/vehicle_brands").then(({ data }) => setBrands(data || [])).catch(() => {});
  }, []);

  const selectedBrandId = brands.find((b) => b.name === form.brand)?.id;
  const modelFilter = selectedBrandId ? (m) => m.brand_id === selectedBrandId : null;

  const canProceed = step === 0
    ? form.license_plate.trim() && form.brand.trim() && form.model.trim()
    : true;

  function next() {
    if (!canProceed) { toast.error("Bitte Pflichtfelder ausfüllen"); return; }
    if (step < VEHICLE_STEPS.length - 1) setStep(step + 1);
    else onSave();
  }

  const allAtOnce = editing;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="vehicle-form-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{editing ? "Bearbeiten" : "Neuer Datensatz"}</div>
            <h3 className="font-display font-semibold text-zinc-50 mt-0.5">{editing ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="vehicle-form-close"><X className="w-5 h-5" /></button>
        </div>

        {!allAtOnce && (
          <div className="px-5 pt-4 pb-2 border-b border-zinc-800">
            <Stepper steps={VEHICLE_STEPS} current={step} onStepClick={(i) => i <= step && setStep(i)} />
          </div>
        )}

        <div className="p-5">
          {(allAtOnce || step === 0) && (
            <FormSection title="Identifikation" description="Kennzeichen, Marke und Modell">
              <Field label="Kennzeichen" required>
                <input value={form.license_plate} onChange={f("license_plate")} data-testid="veh-field-license_plate"
                  className={`${inputCls} font-mono ${!form.license_plate ? "border-red-500/40" : ""}`} />
              </Field>
              <Field label="Identifikationsnummer / FIN">
                <input value={form.vin || ""} onChange={f("vin")} data-testid="veh-field-vin" className={`${inputCls} font-mono`} />
              </Field>
              <Field label="Fahrzeugtyp">
                <MasterSelect listKey="vehicle_types" value={form.vehicle_type}
                  onChange={(v) => setForm({ ...form, vehicle_type: v })}
                  testid="veh-field-vehicle_type" placeholder="— Bitte wählen —" />
              </Field>
              <Field label="Marke" required>
                <MasterSelect listKey="vehicle_brands" value={form.brand}
                  onChange={(v) => setForm({ ...form, brand: v, model: "" })}
                  testid="veh-field-brand" />
              </Field>
              <Field label="Modell" required>
                <MasterSelect listKey="vehicle_models" value={form.model}
                  onChange={(v) => setForm({ ...form, model: v })}
                  filter={modelFilter}
                  disabled={!form.brand}
                  testid="veh-field-model"
                  placeholder={form.brand ? "— Modell wählen —" : "Erst Marke wählen"} />
              </Field>
            </FormSection>
          )}

          {(allAtOnce || step === 0) && (
            <FormSection title="Anhänger" description="Optional — Anhänger-Kennzeichen falls verfügbar">
              <Field label="Anhänger">
                <input value={form.trailer || ""} onChange={f("trailer")} className={inputCls} placeholder="z.B. KO-AB123" />
              </Field>
            </FormSection>
          )}

          {(allAtOnce || step === 1) && (
            <FormSection title="Einsatz" description="Abteilung, Firma, Einsatzort & Stellplatz">
              <Field label="Abteilung *">
                <DepartmentPicker required value={form.department_id} onChange={(id) => setForm({ ...form, department_id: id })} testid="veh-department-picker"/>
              </Field>
              <Field label="Firma (eigene Firma ODER Pool)">
                <FirmSelect value={form.firm_id} onChange={(v) => setForm({ ...form, firm_id: v })} />
              </Field>
              <Field label="Einsatzort">
                <input value={form.workplace || ""} onChange={f("workplace")} data-testid="veh-field-workplace" className={inputCls} />
              </Field>
              <Field label="Stellplatz / Parkplatz">
                <MasterSelect listKey="parking_spots" value={form.parking_place}
                  onChange={(v) => setForm({ ...form, parking_place: v })}
                  placeholder="— Stellplatz wählen —" />
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={f("status")} className={inputCls}>
                  {[["aktiv","Aktiv"],["werkstatt","In Werkstatt"],["inaktiv","Inaktiv"]].map(([k,l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </Field>
            </FormSection>
          )}

          {(allAtOnce || step === 1) && (
            <FormSection title="Termine & Fristen" description="Pickerl, Versicherung, Service, Garantie">
              <Field label="Eingetragenes Datum">
                <input type="date" value={form.registration_date || ""} onChange={f("registration_date")} className={inputCls} />
              </Field>
              <Field label="EZ / §57a Pickerl">
                <input type="date" value={form.pickerl_date || ""} onChange={f("pickerl_date")} className={inputCls} />
              </Field>
              <Field label="Garantielaufzeit (bis)">
                <input type="date" value={form.warranty_expiry || ""} onChange={f("warranty_expiry")} className={inputCls} />
              </Field>
              <Field label="Versicherung bis">
                <input type="date" value={form.insurance_date || ""} onChange={f("insurance_date")} className={inputCls} />
              </Field>
              <Field label="Abmeldedatum">
                <input type="date" value={form.deregistration_date || ""} onChange={f("deregistration_date")} className={inputCls} />
              </Field>
              <Field label="Nächster Service">
                <input type="date" value={form.next_service_date || ""} onChange={f("next_service_date")} className={inputCls} />
              </Field>
            </FormSection>
          )}

          {(allAtOnce || step === 2) && (
            <FormSection title="Technisch" description="Motor, Kraftstoff, Verbrauch">
              <Field label="Leistung (kW)">
                <input type="number" step="0.01" value={form.power_kw || 0} onChange={f("power_kw")} className={`${inputCls} font-mono`} />
              </Field>
              <Field label="Kraftstoff">
                <select value={form.fuel_type || "Diesel"} onChange={f("fuel_type")} className={inputCls}>
                  {FUEL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="AdBlue %">
                <input type="number" min={0} max={100} value={form.adblue_percent || 0} onChange={f("adblue_percent")} className={`${inputCls} font-mono`} />
              </Field>
              <Field label="Letzter Km-Status">
                <input type="number" value={form.current_km || 0} onChange={f("current_km")} className={`${inputCls} font-mono`} />
              </Field>
              <Field label="Verbrauch auf 100 km">
                <input type="number" step="0.01" value={form.consumption_per_100km || 0} onChange={f("consumption_per_100km")} className={`${inputCls} font-mono`} />
              </Field>
              <Field label="Verbrauchs-Toleranz +-">
                <input type="number" step="0.01" value={form.consumption_tolerance || 0} onChange={f("consumption_tolerance")} className={`${inputCls} font-mono`} />
              </Field>
            </FormSection>
          )}

          {(allAtOnce || step === 2) && (
            <FormSection title="Maße & Reifen" description="Innenraum-Maße und Reifendimension">
              <Field label="Reifendimension">
                <input value={form.tire_dimension || ""} onChange={f("tire_dimension")} placeholder="z.B. 215/65/16" className={inputCls} />
              </Field>
              <Field label="Nutzlast (kg)">
                <input type="number" step="0.01" value={form.payload_kg || 0} onChange={f("payload_kg")} className={`${inputCls} font-mono`} />
              </Field>
              <Field label="Volumen m³">
                <input type="number" step="0.01" value={form.volume_m3 || 0} onChange={f("volume_m3")} className={`${inputCls} font-mono`} />
              </Field>
              <Field label="Innere Breite (cm)">
                <input type="number" step="0.1" value={form.inner_width_cm || 0} onChange={f("inner_width_cm")} className={`${inputCls} font-mono`} />
              </Field>
              <Field label="Innere Höhe (cm)">
                <input type="number" step="0.1" value={form.inner_height_cm || 0} onChange={f("inner_height_cm")} className={`${inputCls} font-mono`} />
              </Field>
              <Field label="Innere Länge (cm)">
                <input type="number" step="0.1" value={form.inner_length_cm || 0} onChange={f("inner_length_cm")} className={`${inputCls} font-mono`} />
              </Field>
            </FormSection>
          )}

          {(allAtOnce || step === 3) && (
            <FormSection title="Marketing & Sonderausstattung" description="Branding, Vignette und besondere Merkmale">
              <Field label="Besondere Merkmale" colSpan={2}>
                <textarea rows={2} value={form.special_features || ""} onChange={f("special_features")} placeholder="z.B. Koffer mit Ladebordwand, Aufleger" className={inputCls} />
              </Field>
              <Field label="Branding" colSpan={2}>
                <input value={form.branding || ""} onChange={f("branding")} placeholder="z.B. Fedex GLS Logo" className={inputCls} />
              </Field>
              <Field label="Vignette / Go-Box-Nr.">
                <input value={form.vignette || ""} onChange={f("vignette")} className={inputCls} />
              </Field>
              <Field label="Notizen" colSpan={2}>
                <textarea rows={3} value={form.notes || ""} onChange={f("notes")} className={inputCls} />
              </Field>
            </FormSection>
          )}
        </div>

        <div className="p-5 border-t border-zinc-800 flex justify-between gap-2">
          <div>
            {!allAtOnce && step > 0 && (
              <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-200" data-testid="veh-back-btn">
                <ChevronLeft className="w-4 h-4" /> Zurück
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded">Abbrechen</button>
            {allAtOnce ? (
              <button onClick={onSave} data-testid="veh-save-btn" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
            ) : (
              <button onClick={next} data-testid={step === VEHICLE_STEPS.length - 1 ? "veh-save-btn" : "veh-next-btn"} disabled={!canProceed}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium">
                {step === VEHICLE_STEPS.length - 1 ? "Speichern" : <>Weiter <ChevronRight className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// Inline firm selector — supports a "— Pool (gemeinsam) —" option.
function FirmSelect({ value, onChange }) {
  const [firms, setFirms] = useState([]);
  useEffect(() => {
    api.get("/firms").then(({ data }) => setFirms(data || [])).catch(() => setFirms([]));
  }, []);
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid="veh-firm-select"
      className={inputCls}
    >
      <option value="">— Pool (firmen­übergreifend) —</option>
      {firms.map((f) => (
        <option key={f.id} value={f.id}>{f.name}</option>
      ))}
    </select>
  );
}
